// This file makes "unhandled promise rejection" errors debuggable in
// Honeybadger.
//
// The problem. When a promise rejects and nothing catches it, the browser
// fires an "unhandledrejection" event. That event carries only the rejection
// value. If that value is a string, undefined, or an object without a stack
// trace, Honeybadger shows an error with no stack trace at all. We then have
// no way to tell whether the failure came from our code, an injected
// third-party script, or a browser extension — so this whole class of errors
// was being ignored.
//
// The fix. setupEnhancedPromise() replaces the global Promise class with the
// subclass defined below. Every `new Promise(...)` now records a stack trace
// at the moment the promise is created. If the promise later rejects, that
// recorded stack trace (and the URL of the <script> that was running) gets
// attached to the promise object and to the rejection value. So even when
// the rejection value itself tells us nothing, we still know where the
// promise came from.
//
// Two places read what this file records:
// - The "unhandledrejection" listener in _app.tsx. It reads the recorded
//   stack off the promise object (event.promise) and adds it to the
//   Honeybadger report. This works for every kind of rejection value.
// - honeybadger.common.js. It reads the `__promise_stack` property off Error
//   values. The Honeybadger SDK copies an Error's own properties onto the
//   report, so the recorded stack also survives when some other code path
//   reports the Error object directly.
//
// What this cannot see: async/await and built-in functions like fetch()
// create promises from the engine's internal Promise class, and replacing
// the global cannot intercept those. That is acceptable — those paths
// already produce real Error objects with real stack traces. This file
// exists for the rest: `new Promise(...)`, the Promise statics
// (all/resolve/reject/...), and .then chains that start from one of those.
//
// Cost: recording is cheap because the stack trace is only formatted into a
// string if a rejection actually happens. Primitive resolutions retain no
// snapshot; object/function resolutions retain one because native thenable
// assimilation can reject without calling our reject callback.
//
// One recurring headache handled throughout: browsers format stack traces
// differently. Chrome and Node (the V8 engine) start with a header line like
// "Error" and each frame line starts with "at ". Safari (the JSC engine) has
// no header line, frames look like "name@file:line:col", and built-in
// functions appear as "[native code]".
const ENHANCED_PROMISE_MARKER = "__mt_enhanced_promise"
const PROMISE_SNAPSHOT = "__mt_promise_snapshot"
const PROMISE_CURRENT_SCRIPT = "__mt_current_script"
const PROMISE_STACK = "__promise_stack"
const SNAPSHOT_LEADING_FRAMES = "__mt_leading_frames"
const V8_STACK_FRAME = /^at\s+/
const JSC_STACK_FRAME = /^(?:.*@)?(?:.+:\d+:\d+|\[native code\])$/

function isStackFrame(line: string): boolean {
  return V8_STACK_FRAME.test(line) || JSC_STACK_FRAME.test(line)
}

// True for stack-trace lines that belong to this file rather than to the
// code that created the promise. Matching on names only works in development
// builds — production minification renames everything — so this is a
// second layer of cleanup. In production the frames from this file were
// already removed when the stack was recorded (see capturePromiseSnapshot).
function isPromiseMachineryFrame(frame: string): boolean {
  return (
    frame.includes("enhanced-promise") ||
    frame.includes("capturePromiseSnapshot") ||
    frame.includes("EnhancedPromise") ||
    frame.includes("[native code]")
  )
}

// Returns only the frame lines of a stack trace. Chrome/Node stacks start
// with a header line ("Error" or "Error: some message") that gets dropped;
// Safari stacks have no header, so every line is kept.
export function promiseStackFrames(stack: string): string[] {
  const lines = stack
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
  const firstLine = lines[0]
  if (firstLine === undefined) {
    return []
  }

  return isStackFrame(firstLine) ? lines : lines.slice(1)
}

// Cleans a recorded stack trace for reporting: keeps the header line (if the
// browser produced one) and drops any lines that point at this file, so the
// first frame a person reads is the code that actually created the promise.
export function trimPromiseCreationStack(stack: string | null): string | null {
  if (stack === null) {
    return null
  }

  const lines = stack
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
  const firstLine = lines[0]
  if (firstLine === undefined) {
    return null
  }

  const header = isStackFrame(firstLine) ? [] : [firstLine]
  const frames = promiseStackFrames(stack).filter(
    (frame) => !isPromiseMachineryFrame(frame)
  )
  const trimmedStack = [...header, ...frames].join("\n")
  return trimmedStack.length > 0 ? trimmedStack : null
}

// Records a stack trace for a promise being constructed right now, with the
// frames from this file already excluded.
//
// Excluding our own frames cannot rely on function names: production builds
// minify them away. Instead:
// - On Chrome/Node, Error.captureStackTrace takes a function as its second
//   argument and skips every frame up to and including that function's call.
//   Passing the EnhancedPromise constructor removes our frames by position in
//   the call chain, no names involved.
// - On Safari there is no captureStackTrace, so we take a full `new Error()`
//   stack and mark it with the number of leading frames that belong to us
//   (this helper plus the constructor: 2). promiseSnapshotStack drops that
//   many lines when the stack is read later.
//
// The returned object is NOT a formatted string. Reading `.stack` is the
// expensive part, and it only happens if the promise actually rejects.
// Error.stackTraceLimit is clamped so the eventual formatting cost is small.
function capturePromiseSnapshot(
  promiseConstructor: Parameters<typeof Error.captureStackTrace>[1]
): object {
  const originalLimit = Error.stackTraceLimit
  if (typeof originalLimit === "number") {
    Error.stackTraceLimit = Math.min(originalLimit, 6)
  }

  try {
    if (typeof Error.captureStackTrace === "function") {
      const snapshot = {}
      Error.captureStackTrace(snapshot, promiseConstructor)
      return snapshot
    }

    const snapshot = new Error()
    Reflect.set(snapshot, SNAPSHOT_LEADING_FRAMES, 2)
    return snapshot
  } finally {
    if (typeof originalLimit === "number") {
      Error.stackTraceLimit = originalLimit
    }
  }
}

// URL of the <script> tag whose code is running right now, if any. When a
// promise is created during a script's initial top-level run, this names the
// exact file — which is often a browser extension or an injected third-party
// script, precisely the cases where the stack trace tells us nothing. It is
// null on the server, inside event handlers, and inside async callbacks,
// because by then no script tag is "currently executing".
function currentScriptSource(): string | null {
  if (typeof document === "undefined" || document.currentScript === null) {
    return null
  }

  const source = Reflect.get(document.currentScript, "src")
  return typeof source === "string" && source.length > 0 ? source : null
}

// Turns a recorded snapshot into a stack-trace string. Reading `.stack` here
// is what makes the engine format the trace — deliberately deferred to this
// point so promises that never reject never pay for it.
export function promiseSnapshotStack(snapshot: unknown): string | null {
  if (
    snapshot === null ||
    (typeof snapshot !== "object" && typeof snapshot !== "function")
  ) {
    return null
  }

  const stack = Reflect.get(snapshot, "stack")
  if (typeof stack !== "string") {
    return null
  }

  const leadingFrames = Reflect.get(snapshot, SNAPSHOT_LEADING_FRAMES)
  if (typeof leadingFrames !== "number" || leadingFrames === 0) {
    return stack
  }

  // A leading-frames marker means this snapshot came from the Safari path in
  // capturePromiseSnapshot: the first N lines are our own code and must be
  // dropped. Safari stacks have no header line, so dropping by line count is
  // safe.
  const remaining = stack
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(leadingFrames)
    .join("\n")
  return remaining.length > 0 ? remaining : null
}

// Stores the recorded stack and script URL on the promise object itself.
// The unhandledrejection listener in _app.tsx picks them up from
// event.promise.
function attachPromiseEvidence(
  promise: Promise<unknown>,
  snapshot: object,
  scriptSource: string | null
): void {
  Reflect.set(promise, PROMISE_SNAPSHOT, snapshot)
  Reflect.set(promise, PROMISE_CURRENT_SCRIPT, scriptSource)
}

// When the rejection value is an Error, also store the recorded stack on the
// Error itself as `__promise_stack` (read by honeybadger.common.js). First
// write wins: an Error re-thrown through several promises keeps the stack of
// the promise closest to where it was originally created.
function setFirstPromiseStack(reason: unknown, snapshot: object): void {
  if (!(reason instanceof Error) || Reflect.has(reason, PROMISE_STACK)) {
    return
  }

  const stack = promiseSnapshotStack(snapshot)
  if (stack !== null) {
    Reflect.set(reason, PROMISE_STACK, stack)
  }
}

// Replaces the global Promise class with the recording subclass. Safe to
// call more than once: the marker on the installed class makes repeat calls
// do nothing.
export function setupEnhancedPromise(): void {
  const OriginalPromise = globalThis.Promise
  if (Reflect.get(OriginalPromise, ENHANCED_PROMISE_MARKER) === true) {
    return
  }

  class EnhancedPromise<T> extends OriginalPromise<T> {
    constructor(
      executor: (
        resolve: (value: T | PromiseLike<T>) => void,
        reject: (reason?: unknown) => void
      ) => void
    ) {
      const snapshot = capturePromiseSnapshot(EnhancedPromise)
      const scriptSource = currentScriptSource()
      let attachEvidenceToPromise: (() => void) | null = null
      let pendingAttach = false

      // The recorded stack is stored on the promise only if the promise can
      // still end up rejected. Primitive resolutions keep no snapshot.
      // Objects and functions can reject during native thenable assimilation,
      // so even fulfilled object resolutions retain creation evidence.
      //
      // One wrinkle: the executor callback runs inside the super() call
      // below, before `this` exists. If the executor rejects synchronously,
      // we can't store onto `this` yet — so we set a flag and store right
      // after super() returns.
      const attachEvidence = () => {
        if (attachEvidenceToPromise === null) {
          pendingAttach = true
          return
        }

        attachEvidenceToPromise()
      }

      super((resolve, reject) => {
        const enhancedReject = (reason?: unknown) => {
          setFirstPromiseStack(reason, snapshot)
          attachEvidence()
          reject(reason)
        }
        // resolve() can also lead to a rejection: when code calls
        // resolve(someOtherPromise) and that other promise rejects, this
        // promise rejects too — but the engine does that internally, without
        // ever calling our wrapped reject. Only the native resolver may read
        // .then: getters can throw or change values between reads.
        const enhancedResolve = (value: T | PromiseLike<T>) => {
          if (
            value !== null &&
            (typeof value === "object" || typeof value === "function")
          ) {
            attachEvidence()
          }
          resolve(value)
        }

        try {
          executor(enhancedResolve, enhancedReject)
        } catch (error) {
          enhancedReject(error)
        }
      })

      attachEvidenceToPromise = () =>
        attachPromiseEvidence(this, snapshot, scriptSource)
      if (pendingAttach) {
        attachEvidence()
      }
    }

    // Keeps `instanceof Promise` working for promises we didn't create.
    // async functions and fetch() return promises built from the engine's
    // internal Promise class, not this subclass — without this override,
    // `(async () => {})() instanceof Promise` would be false after the global
    // is replaced.
    static [Symbol.hasInstance](value: unknown): boolean {
      return value instanceof OriginalPromise
    }
  }

  Reflect.set(EnhancedPromise, ENHANCED_PROMISE_MARKER, true)
  Object.defineProperty(globalThis, "Promise", {
    configurable: true,
    value: EnhancedPromise,
    writable: true,
  })
}
